import React, { useRef, useState, useEffect } from "react";

const Section2 = ({ url }) => {
  const sectionRef = useRef(null);
  const [Slick, setSlick] = useState(null);
  const slickLoaded = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !slickLoaded.current) {
          slickLoaded.current = true;
          const slickModule = await import("react-slick");
          await import("slick-carousel/slick/slick.css");
          await import("slick-carousel/slick/slick-theme.css");
          setSlick(() => slickModule.default);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    speed: 600,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 2 } },
      { breakpoint: 768,  settings: { slidesToShow: 1 } },
    ],
  };

 const cards = [
    {
        image: `${url}images/aiDevelopment/sec3/1.png`,
        title: "AI Integration & API Development",
        desc: "Deploy the power of leading AI models, including OpenAI, Google Gemini, and Anthropic Claude, directly into your existing software, workflows, and customer touchpoints. We handle architecture, security, context management, and scalability so the integration is robust, not fragile.",
        bullets: [
        "Connect AI capabilities to your existing systems via secure, scalable API architecture",
        "Context-aware implementations that understand your business data and terminology",
        "Model-agnostic approach: built to adapt as AI technology evolves",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/2.png`,
        title: "Business Process Automation",
        desc: "Identify the manual, repetitive workflows that consume your team's time and eliminate them with intelligent automation. From multi-step approval processes to data extraction, transformation, and routing, we build automation that works reliably at scale.",
        bullets: [
        "End-to-end workflow automation across departments and systems",
        "Intelligent document processing: extract, classify, and route data automatically",
        "Error reduction and audit trails built into every automated process",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/3.png`,
        title: "AI-Powered Chatbots & Virtual Assistants",
        desc: "Build conversational AI experiences that actually resolve user needs, whether customer-facing support, internal helpdesk automation, or sales qualification flows. Our chatbots are context-aware, trained on your data, and integrated with your systems for real resolution, not scripted deflection.",
        bullets: [
        "Customer support bots that resolve queries without human escalation",
        "Internal AI assistants for HR, IT, and operations queries",
        "Sales qualification and lead routing automation via conversational AI",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/4.png`,
        title: "Robotic Process Automation (RPA)",
        desc: "Automate structured, rule-based tasks across your existing applications without requiring system-level integration. RPA sits on top of your current tools, mimicking human interaction to execute repetitive processes at speed and without error.",
        bullets: [
        "Data migration, extraction, and reconciliation across legacy systems",
        "Automated reporting: pull, compile, and distribute reports on schedule",
        "Cross-system data synchronization without expensive custom integrations",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/5.png`,
        title: "Custom AI Model Development",
        desc: "When your problem requires more than an API integration, we build and fine-tune AI models on your proprietary data. This is for businesses where generic models lack the domain-specific accuracy your operations require.",
        bullets: [
        "Fine-tuned language models trained on your industry terminology and data",
        "Predictive models for demand forecasting, risk scoring, and anomaly detection",
        "Computer vision solutions for quality control, document scanning, and classification",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/6.png`,
        title: "Machine Learning & Predictive Analytics",
        desc: "Turn your historical business data into forward-looking intelligence. We build ML pipelines that identify patterns, forecast outcomes, and surface insights your team can act on, embedded directly into your reporting or operational systems.",
        bullets: [
        "Demand forecasting and inventory optimization",
        "Customer churn prediction and retention scoring",
        "Fraud detection, risk assessment, and anomaly alerting",
        ],
    },
    {
        image: `${url}images/aiDevelopment/sec3/7.png`,
        title: "Agentic AI & Multi-Agent Systems",
        desc: "The cutting edge of AI deployment. Agentic systems are AI models that can plan, reason, use tools, and execute multi-step tasks autonomously. We design and build multi-agent architectures for businesses ready to deploy AI that does more than respond, it acts.",
        bullets: [
        "Autonomous research, analysis, and reporting agents",
        "Multi-agent orchestration for complex, multi-step operational workflows",
        "AI agents integrated with your existing tools, APIs, and data sources",
        ],
    },
    ];

  return (
    <section className="home-section-3 overflow-hidden" ref={sectionRef}>
      <div className="container overflow-hidden">

        <h2 className="s3-title" data-aos="fade-up">
          Custom AI Software Development Company Built Around Your Outcomes
        </h2>

        <p className="s3-subtitle" data-aos="fade-up" data-aos-delay="100">
          ExD Digital designs and develops AI systems from the ground up, primarily on the OpenAI
          ecosystem, with the flexibility to work with any model or framework your requirements
          demand. Every solution is built to integrate with your existing systems, not sit beside them.
        </p>

            {/* Slider — full width */}
            <div className="s3-slider-wrapper" data-aos="fade-up" data-aos-delay="200">
                {Slick ? (
                <Slick {...settings}>
                    {cards.map((card, idx) => (
                    <div className="s3-slide-pad" key={idx}>
                        <div className="s3-card">
                        <div className="s3-card-image">
                            <img src={card.image} alt={card.title} />
                        </div>
                        <div className="s3-card-body">
                            <h3 className="s3-card-title">{card.title}</h3>
                            <p className="s3-card-desc">{card.desc}</p>
                            <ul className="s3-card-bullets">
                            {card.bullets.map((b, i) => (
                                <li key={i}>{b}</li>
                            ))}
                            </ul>
                        </div>
                        </div>
                    </div>
                    ))}
                </Slick>
                ) : (
                // skeleton placeholders while slick loads
                <div className="s3-skeleton-row">
                    {[1, 2, 3].map((i) => (
                    <div className="s3-skeleton-card" key={i} />
                    ))}
                </div>
                )}
            </div>
        </div>
    </section>
  );
};

export default Section2;